import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    ArrowLeft,
    Loader2,
    AlertTriangle,
    House,
    Plus,
    FileText,
    MapPinned
} from 'lucide-react';

// ✅ Поправьте путь, если перенесете файлы в другую папку
import {
    generateProgrammingCard,
    generateProgrammingCardTypeB
} from './generateProgrammingCard';

import {
    API_URL,
    DEVICE_TYPE_MAP
} from './houseHelpers';

import { useToast } from './toastContext';

import MeteringNodeCard from './MeteringNodeCard';

import EditNodeStatusModal from './EditNodeStatusModal';
import NodeFormModal from './NodeFormModal';
import DeleteNodeConfirmModal from './DeleteNodeConfirmModal';
import DeleteDeviceConfirmModal from './DeleteDeviceConfirmModal';
import EditDeviceModal from './EditDeviceModal';
import CreateDeviceModal from './CreateDeviceModal';
import ProgrammingCardModal from './ProgrammingCardModal';
import UploadActModal from './UploadActModal';
import ViewActsModal from './ViewActsModal';

const HousePageContent = ({
    city,
    street,
    house,
    onBack,
    isDesktop = false,
    onSelectDevice,
    onSelect
}) => {
    const houseNumber = house.replace('Дом ', '').trim();

    const toast = useToast();

    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    const canChangeHouses = userInfo.canChangeHousesYY === 1;

    const [houseData, setHouseData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const [editStatusNode, setEditStatusNode] = useState(null);
    const [editDevice, setEditDevice] = useState(null);
    const [nodeFormModal, setNodeFormModal] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [createDeviceModal, setCreateDeviceModal] = useState(null);
    const [deleteDeviceConfirm, setDeleteDeviceConfirm] = useState(null);
    const [programmingCardModal, setProgrammingCardModal] = useState(null);
    const [uploadActModal, setUploadActModal] = useState(null);
    const [viewActsModal, setViewActsModal] = useState(null);
    const [savedViewActsModal, setSavedViewActsModal] = useState(null);

    useEffect(() => {
        loadHouse();
    }, [city, street, houseNumber]);

    const loadHouse = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');

            const response = await axios.get(
                `${API_URL}/api/houses/${encodeURIComponent(city)}/${encodeURIComponent(street)}/${encodeURIComponent(houseNumber)}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            if (!response.data) {
                setError('Дом не найден в базе данных');
                return;
            }

            setHouseData(response.data);
        } catch (err) {
            console.error('❌ Ошибка загрузки дома:', err);

            if (err.response) {
                switch (err.response.status) {
                    case 404:
                        toast.error('Дом не найден в базе данных');
                        break;

                    case 401:
                        toast.error('Необходима авторизация');
                        break;

                    case 500:
                        toast.error('Ошибка сервера');
                        break;

                    default:
                        toast.error(err.response.data?.message || `Ошибка ${err.response.status}`);
                }
            } else if (err.request) {
                toast.error('Не удалось связаться с сервером');
            } else {
                toast.error(err.message || 'Не удалось загрузить данные дома');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveStatus = async (nodeId, data) => {
        try {
            const token = localStorage.getItem('token');

            await axios.put(
                `${API_URL}/api/houses/nodes/${nodeId}/status`,
                data,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            setEditStatusNode(null);
            loadHouse();

            toast.success('Статус узла сохранён');
        } catch (err) {
            console.error('Ошибка сохранения статуса узла:', err);
            toast.error(err.response?.data?.message || 'Не удалось сохранить статус узла');
        }
    };

    const handleSaveDevice = async (type, deviceId, data) => {
        try {
            const token = localStorage.getItem('token');

            let endpoint;

            switch (type) {
                case 'heat-computer':
                    endpoint = `/api/houses/heat-computers/${deviceId}`;
                    break;

                case 'temp-sensor':
                    endpoint = `/api/houses/temperature-sensors/${deviceId}`;
                    break;

                case 'pressure-sensor':
                    endpoint = `/api/houses/pressure-sensors/${deviceId}`;
                    break;

                case 'flow-meter':
                    endpoint = `/api/houses/flow-meters/${deviceId}`;
                    break;

                default:
                    return;
            }

            await axios.put(`${API_URL}${endpoint}`, data, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            setEditDevice(null);
            loadHouse();

            toast.success('Данные устройства сохранены');
        } catch (err) {
            console.error('Ошибка сохранения устройства:', err);
            toast.error('Не удалось сохранить данные устройства');
        }
    };

    const handleCreateNode = async (data) => {
        try {
            const token = localStorage.getItem('token');

            await axios.post(
                `${API_URL}/api/houses/${encodeURIComponent(city)}/${encodeURIComponent(street)}/${encodeURIComponent(houseNumber)}/nodes`,
                data,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            setNodeFormModal(null);
            loadHouse();

            toast.success('Узел учёта создан');
        } catch (err) {
            console.error('Ошибка создания узла:', err);
            toast.error(err.response?.data?.message || 'Не удалось создать узел учёта');
        }
    };

    const handleUpdateNode = async (nodeId, data) => {
        try {
            const token = localStorage.getItem('token');

            await axios.put(`${API_URL}/api/houses/nodes/${nodeId}`, data, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            setNodeFormModal(null);
            loadHouse();

            toast.success('Узел учёта обновлён');
        } catch (err) {
            console.error('Ошибка обновления узла:', err);
            toast.error(err.response?.data?.message || 'Не удалось обновить узел учёта');
        }
    };

    const handleDeleteNode = async (nodeId) => {
        try {
            const token = localStorage.getItem('token');

            await axios.delete(`${API_URL}/api/houses/nodes/${nodeId}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            setDeleteConfirm(null);
            loadHouse();

            toast.success('Узел учёта удалён');
        } catch (err) {
            console.error('Ошибка удаления узла:', err);
            toast.error(err.response?.data?.message || 'Не удалось удалить узел учёта');
        }
    };

    const handleCreateDevice = async (nodeId, type, data) => {
        try {
            const payload = JSON.parse(JSON.stringify(data));

            if (payload.factoryNumber !== undefined && payload.factoryNumber !== null) {
                payload.factoryNumber = String(payload.factoryNumber).trim();
            } else {
                payload.factoryNumber = null;
            }

            payload.deviceType = payload.deviceType ? String(payload.deviceType).trim() : '';
            payload.deviceName = payload.deviceName ? String(payload.deviceName).trim() : '';

            const dateFields = [
                'lastEditTime',
                'verificationDate',
                'oplombirovanDate',
                'verificationValidUntil',
                'releaseDate'
            ];

            dateFields.forEach((field) => {
                if (
                    payload[field] &&
                    typeof payload[field] === 'string' &&
                    payload[field].includes('.')
                ) {
                    const [datePart] = payload[field].split(' ');
                    const [day, month, year] = datePart.split('.');

                    payload[field] = `${year}-${month}-${day}`;
                }
            });

            const token = localStorage.getItem('token');

            let endpoint;

            switch (type) {
                case 'heat-computer':
                    endpoint = `/api/houses/nodes/${nodeId}/heat-computers`;
                    break;

                case 'temp-sensor':
                    endpoint = `/api/houses/nodes/${nodeId}/temperature-sensors`;
                    break;

                case 'pressure-sensor':
                    endpoint = `/api/houses/nodes/${nodeId}/pressure-sensors`;
                    break;

                case 'flow-meter':
                    endpoint = `/api/houses/nodes/${nodeId}/flow-meters`;
                    break;

                default:
                    return;
            }

            await axios.post(`${API_URL}${endpoint}`, payload, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            setCreateDeviceModal(null);
            loadHouse();

            toast.success('Устройство создано');
        } catch (err) {
            console.error('Ошибка создания устройства:', err);

            let errorMsg = 'Не удалось создать устройство';

            if (err.response?.data?.errors) {
                errorMsg = Object.values(err.response.data.errors).flat().join('; ');
            } else if (err.response?.data?.message) {
                errorMsg = err.response.data.message;
            }

            toast.error(errorMsg);
        }
    };

    const handleDeleteDevice = async (type, deviceId) => {
        try {
            const token = localStorage.getItem('token');

            let endpoint;

            switch (type) {
                case 'heat-computer':
                    endpoint = `/api/houses/heat-computers/${deviceId}`;
                    break;

                case 'temp-sensor':
                    endpoint = `/api/houses/temperature-sensors/${deviceId}`;
                    break;

                case 'pressure-sensor':
                    endpoint = `/api/houses/pressure-sensors/${deviceId}`;
                    break;

                case 'flow-meter':
                    endpoint = `/api/houses/flow-meters/${deviceId}`;
                    break;

                default:
                    return;
            }

            await axios.delete(`${API_URL}${endpoint}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            setDeleteDeviceConfirm(null);
            loadHouse();

            toast.success('Устройство удалено');
        } catch (err) {
            console.error('Ошибка удаления устройства:', err);
            toast.error(err.response?.data?.message || 'Не удалось удалить устройство');
        }
    };

    const handleGoToDevice = (heatComputer) => {
        if (!onSelectDevice || !heatComputer) return;

        const deviceTypeRu = DEVICE_TYPE_MAP[heatComputer.deviceType] || heatComputer.deviceType;

        const device = {
            ...heatComputer,
            name: heatComputer.deviceName,
            deviceType: deviceTypeRu,
            fullName: `${deviceTypeRu} ${heatComputer.deviceName}`,
            city,
            street,
            houseNumber,
            status: 0
        };

        onSelectDevice({
            type: 'device',
            data: device
        });
    };

    const handleUploadAct = async (nodeId, actType, file) => {
        try {
            const token = localStorage.getItem('token');

            const formData = new FormData();
            formData.append('file', file);

            await axios.post(
                `${API_URL}/api/houses/nodes/${nodeId}/acts/${actType}`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            toast.success('Документ успешно загружен');
        } catch (err) {
            console.error('Ошибка загрузки документа:', err);

            let errorMessage = 'Не удалось загрузить документ';

            if (err.response?.data) {
                if (err.response.data.message) {
                    errorMessage = err.response.data.message;
                } else if (err.response.data.detail) {
                    errorMessage = err.response.data.detail;
                } else if (err.response.data.errors) {
                    const errors = err.response.data.errors;
                    const firstKey = Object.keys(errors)[0];

                    errorMessage = errors[firstKey][0];
                } else if (typeof err.response.data === 'string') {
                    errorMessage = err.response.data;
                }
            } else if (err.message) {
                errorMessage = err.message;
            }

            toast.error(errorMessage);
        }
    };

    const handleDeleteAct = async (actId, nodeId) => {
        try {
            const token = localStorage.getItem('token');

            await axios.delete(`${API_URL}/api/houses/nodes/acts/${actId}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            toast.success('Документ удалён');

            if (viewActsModal) {
                const response = await axios.get(`${API_URL}/api/houses/nodes/${nodeId}/acts`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });

                setViewActsModal({
                    nodeId,
                    acts: response.data
                });
            }
        } catch (err) {
            console.error('Ошибка удаления документа:', err);
            toast.error(err.response?.data?.message || 'Не удалось удалить документ');
        }
    };

    const handleViewActs = async (nodeId) => {
        try {
            const token = localStorage.getItem('token');

            const response = await axios.get(`${API_URL}/api/houses/nodes/${nodeId}/acts`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            setViewActsModal({
                nodeId,
                acts: response.data
            });
        } catch (err) {
            console.error('Ошибка загрузки документов:', err);
            toast.error('Не удалось загрузить документы');
        }
    };

    const handleGenerateProgrammingCard = async (node) => {
        const success = await generateProgrammingCard(node, city, street, house, toast);

        if (success) {
            setProgrammingCardModal(null);
        }
    };

    const handleGenerateProgrammingCardTypeB = async (node) => {
        const success = await generateProgrammingCardTypeB(node, city, street, house, toast);

        if (success) {
            setProgrammingCardModal(null);
        }
    };

    if (isLoading) {
        return (
            <div className="h-full flex items-center justify-center">
                <Loader2 className="animate-spin h-8 w-8 text-blue-500" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="h-full flex items-center justify-center p-4">
                <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    <span>{error}</span>
                </div>
            </div>
        );
    }

    const nodes = houseData?.meteringNodes || [];

    return (
        <div className="h-full flex flex-col">
            <div className="bg-slate-800 border-b border-slate-700 p-4">
                {!isDesktop && (
                    <div className="flex items-center gap-3 mb-3">
                        <button
                            onClick={onBack}
                            className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition text-sm"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Назад
                        </button>
                    </div>
                )}

                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <h2 className="flex items-center gap-2 text-lg sm:text-xl font-bold text-white">
                            <House className="w-6 h-6 shrink-0" />
                            <span>{house}</span>
                        </h2>

                        <div className="flex items-center gap-2 text-slate-400 text-sm flex-wrap mt-1">
                            <span>{city},</span>

                            <button
                                onClick={() => {
                                    if (onSelect) {
                                        onSelect({
                                            type: 'street',
                                            data: { city, street }
                                        });
                                    } else {
                                        console.warn('⚠️ Не передан обработчик onSelect для перехода на улицу');
                                    }
                                }}
                                className="flex items-center gap-1 text-cyan-400 hover:text-cyan-300 hover:underline transition-colors rounded px-1 -ml-1"
                                title="Перейти к списку домов на этой улице"
                            >
                                <MapPinned className="w-3.5 h-3.5" />
                                <span>{street}</span>
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                        {canChangeHouses && nodes.length > 0 && (
                            <button
                                onClick={() =>
                                    setProgrammingCardModal({
                                        node: nodes.length === 1 ? nodes[0] : null
                                    })
                                }
                                className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-sm font-medium transition"
                            >
                                <FileText className="w-4 h-4" />
                                Карта программирования
                            </button>
                        )}

                        {canChangeHouses && (
                            <button
                                onClick={() => setNodeFormModal({ mode: 'create' })}
                                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm font-medium transition"
                            >
                                <Plus className="w-4 h-4" />
                                Добавить узел
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar p-4 sm:p-6 space-y-4">
                {nodes.length === 0 ? (
                    <div className="bg-slate-800 rounded-xl p-12 border border-slate-700 text-center">
                        <p className="text-slate-400 mb-4">
                            В этом доме нет узлов учёта
                        </p>

                        {canChangeHouses && (
                            <button
                                onClick={() => setNodeFormModal({ mode: 'create' })}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm font-medium transition"
                            >
                                <Plus className="w-4 h-4" />
                                Создать первый узел
                            </button>
                        )}
                    </div>
                ) : (
                    nodes.map((node) => (
                        <MeteringNodeCard
                            key={node.id}
                            node={node}
                            canChangeHouses={canChangeHouses}
                            onEditStatus={() => setEditStatusNode(node)}
                            onEditNode={() => setNodeFormModal({ mode: 'edit', node })}
                            onDeleteNode={() =>
                                setDeleteConfirm({
                                    nodeId: node.id,
                                    nodeName: node.name
                                })
                            }
                            onEditDevice={(type, data) => setEditDevice({ type, data })}
                            onCreateDevice={(type) =>
                                setCreateDeviceModal({
                                    nodeId: node.id,
                                    type
                                })
                            }
                            onDeleteDevice={(type, deviceId, name, specificType) =>
                                setDeleteDeviceConfirm({
                                    type,
                                    deviceId,
                                    deviceName: name,
                                    specificType
                                })
                            }
                            onGoToDevice={handleGoToDevice}
                            onGenerateCard={() => setProgrammingCardModal({ node })}
                            onViewActs={() => handleViewActs(node.id)}
                        />
                    ))
                )}
            </div>

            {editStatusNode && (
                <EditNodeStatusModal
                    node={editStatusNode}
                    onClose={() => setEditStatusNode(null)}
                    onSave={(data) => handleSaveStatus(editStatusNode.id, data)}
                />
            )}

            {editDevice && (
                <EditDeviceModal
                    type={editDevice.type}
                    device={editDevice.data}
                    onClose={() => setEditDevice(null)}
                    onSave={(data) =>
                        handleSaveDevice(editDevice.type, editDevice.data.id, data)
                    }
                />
            )}

            {nodeFormModal && (
                <NodeFormModal
                    mode={nodeFormModal.mode}
                    node={nodeFormModal.node}
                    onClose={() => setNodeFormModal(null)}
                    onSave={(data) => {
                        if (nodeFormModal.mode === 'create') {
                            handleCreateNode(data);
                        } else {
                            handleUpdateNode(nodeFormModal.node.id, data);
                        }
                    }}
                />
            )}

            {deleteConfirm && (
                <DeleteNodeConfirmModal
                    nodeName={deleteConfirm.nodeName}
                    onClose={() => setDeleteConfirm(null)}
                    onConfirm={() => handleDeleteNode(deleteConfirm.nodeId)}
                />
            )}

            {createDeviceModal && (
                <CreateDeviceModal
                    type={createDeviceModal.type}
                    city={city}
                    street={street}
                    house={house}
                    onClose={() => setCreateDeviceModal(null)}
                    onSave={(data) =>
                        handleCreateDevice(createDeviceModal.nodeId, createDeviceModal.type, data)
                    }
                />
            )}

            {deleteDeviceConfirm && (
                <DeleteDeviceConfirmModal
                    deviceName={deleteDeviceConfirm.deviceName}
                    deviceType={deleteDeviceConfirm.specificType}
                    sectionType={deleteDeviceConfirm.type}
                    onClose={() => setDeleteDeviceConfirm(null)}
                    onConfirm={() =>
                        handleDeleteDevice(deleteDeviceConfirm.type, deleteDeviceConfirm.deviceId)
                    }
                />
            )}

            {programmingCardModal && (
                <ProgrammingCardModal
                    nodes={nodes}
                    selectedNode={programmingCardModal.node}
                    onClose={() => setProgrammingCardModal(null)}
                    onGenerate={handleGenerateProgrammingCard}
                    onGenerateTypeB={handleGenerateProgrammingCardTypeB}
                />
            )}

            {uploadActModal && (
                <UploadActModal
                    actType={uploadActModal.actType}
                    onClose={() => {
                        setUploadActModal(null);

                        if (savedViewActsModal) {
                            handleViewActs(savedViewActsModal.nodeId);
                            setSavedViewActsModal(null);
                        }
                    }}
                    onUpload={async (file) => {
                        await handleUploadAct(uploadActModal.nodeId, uploadActModal.actType, file);

                        setUploadActModal(null);

                        if (savedViewActsModal) {
                            await handleViewActs(savedViewActsModal.nodeId);
                            setSavedViewActsModal(null);
                        }
                    }}
                />
            )}

            {viewActsModal && (
                <ViewActsModal
                    nodeId={viewActsModal.nodeId}
                    acts={viewActsModal.acts}
                    canChangeHouses={canChangeHouses}
                    onClose={() => setViewActsModal(null)}
                    onUploadAct={(actType) => {
                        setSavedViewActsModal(viewActsModal);
                        setViewActsModal(null);

                        setUploadActModal({
                            nodeId: viewActsModal.nodeId,
                            actType
                        });
                    }}
                    onDeleteAct={(actId) => handleDeleteAct(actId, viewActsModal.nodeId)}
                />
            )}
        </div>
    );
};

export default HousePageContent;